import React, { useState, useCallback, memo, useEffect } from 'react';
import { Helmet } from 'react-helmet-async';
import { ErrorBoundary } from '@/components/ErrorBoundary';
import { EmptyState } from '@/components/supply-gpu';
import { useAuth } from '@/contexts/AuthWrapper';
import styles from '@/styles/pages/SupplyGpuPage.module.css';
import galleryStyles from '@/styles/components/models-gallery/ModelsGalleryPage.module.css';


// import Button from '@/components/ui/Button';
// import { useToast } from '@/components/Toast/ToastProvider';
// import { useMachines } from '@/hooks/api/useMachines';
// import { EmptyState, MachineCard, MachineCardSkeleton, PageHeaderSkeleton } from '@/components/supply-gpu';
// import { Machine } from '@/types/supply-gpu';
// import { copyToClipboard, getClipboardErrorMessage } from '@/utils/clipboard';

const SupplyGpuPage: React.FC = memo(() => {
  const { isAuthenticated } = useAuth();
  const [isAddModalOpen, setIsAddModalOpen] = useState(false);

  // const { addToast } = useToast();
  // const { data: machines, isLoading, error, refetch } = useMachines();

  const handleOpenAddModal = useCallback(() => {
    setIsAddModalOpen(true);
  }, []);

  const handleCloseAddModal = useCallback(() => {
    setIsAddModalOpen(false);
  }, []);

  useEffect(() => {
    if (!isAddModalOpen) return undefined;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.preventDefault();
        setIsAddModalOpen(false);
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [isAddModalOpen]);

  // Mock machines data 
  // const mockMachines: Machine[] = [
  //   {
  //     id: '1',
  //     model: 'RTX 4090',
  //     status: 'connected',
  //     specs: {
  //       tflops: 83.0,
  //       memory: 24,
  //       cudaVersion: '12.1',
  //       uploadSpeed: 1000,
  //       downloadSpeed: 1500,
  //     },
  //     createdAt: new Date('2024-01-01'),
  //     updatedAt: new Date('2024-01-01'),
  //   },
  //   {
  //     id: '2',
  //     model: 'RTX 3080',
  //     status: 'pending',
  //     connectionCommand: 'docker run --gpus all swarmind/gpu-worker --token=abc123def456',
  //     createdAt: new Date('2024-01-01'),
  //     updatedAt: new Date('2024-01-01'),
  //   },
  // ];

  // const handleCopyCommand = useCallback(async (command: string) => {
  //   const result = await copyToClipboard(command);
  //   if (result.success) {
  //     addToast({ type: 'success', message: 'Connection command copied to clipboard', duration: 3000 });
  //   } else {
  //     const errorMessage = getClipboardErrorMessage(result.error);
  //     addToast({ type: 'error', message: errorMessage, duration: 5000 });
  //     console.error('Failed to copy command:', result.error);
  //   }
  // }, [addToast]);

  const renderContent = () => {
    // Only show EmptyState (Connect the machine card) if user is logged in
    if (isAuthenticated) {
      return <EmptyState onAddMachine={handleOpenAddModal} />;
    }
    // Return null or empty div if user is not logged in
    return null;
  };

  // const renderContent = () => {
  //   if (isLoading) {
  //     return (
  //       <div 
  //         className={styles.machinesGrid}
  //         role="status"
  //         aria-live="polite"
  //         aria-label="Loading machines"
  //       >
  //         {Array.from({ length: 3 }, (_, index) => (
  //           <MachineCardSkeleton key={`skeleton-${index}`} />
  //         ))}
  //       </div>
  //     );
  //   }
  //
  //   if (error) {
  //     return (
  //       <div 
  //         className={styles.errorContainer}
  //         role="alert"
  //         aria-live="assertive"
  //       >
  //         <p className={styles.errorMessage}>
  //           Failed to load machines. Please try again.
  //         </p>
  //         <Button
  //           variant="outline"
  //           size="sm"
  //           onClick={() => refetch()}
  //           className={styles.retryButton}
  //           aria-label="Retry loading machines"
  //         >
  //           Retry
  //         </Button>
  //       </div>
  //     );
  //   }
  //
  //   // Use machines from API. The API returns an object, not an array.
  //   // Until the backend returns an array of machines compatible with `MachineCard`,
  //   // keep the list empty to avoid type/runtime errors.
  //   const displayMachines: Machine[] = Array.isArray(machines) ? (machines as Machine[]) : [];
  //
  //   if (!displayMachines || displayMachines.length === 0) {
  //     return <EmptyState onAddMachine={handleOpenAddModal} />;
  //   }
  //
  //   return (
  //     <div 
  //       className={styles.machinesGrid}
  //       role="grid"
  //       aria-label={`${displayMachines.length} GPU machines`}
  //       aria-rowcount={displayMachines.length}
  //     >
  //       {displayMachines.map((machine: Machine, index) => (
  //         <MachineCard
  //           key={machine.id}
  //           machine={machine}
  //           onCopyCommand={handleCopyCommand}
  //           aria-rowindex={index + 1}
  //         />
  //       ))}
  //     </div>
  //   );
  // };

  return (
    <ErrorBoundary>
      <Helmet>
        <title>Supply GPU - Swarmind</title>
        <meta name="description" content="Supply GPU to Swarmind - Web3 inference managment system. Share your GPU resources and help power decentralized AI." />
        <meta property="og:title" content="Supply GPU - Swarmind" />
        <meta property="og:description" content="Supply GPU to Swarmind - Web3 inference managment system. Share your GPU resources and help power decentralized AI." />
        <meta property="og:url" content="https://swarmind.ai/supply-gpu" />
        <meta property="og:type" content="website" />
        <meta property="twitter:title" content="Supply GPU - Swarmind" />
        <meta property="twitter:description" content="Supply GPU to Swarmind - Web3 inference managment system. Share your GPU resources and help power decentralized AI." />
        <meta property="twitter:url" content="https://swarmind.ai/supply-gpu" />
      </Helmet>

      {isAuthenticated && (
        <div className={styles.container}>
          <header className={styles.header}>
            <h1 
              className={styles.title}
              id="page-title"
            >
              Supply GPU
            </h1>
            <div className={styles.buttonGroup}>
              <button
                className={styles.addButton}
                onClick={handleOpenAddModal}
                aria-describedby="page-title"
              >
                <img alt="" className={styles.addIcon} src="/icons/left_icon.svg" />
                <div>Add a machine</div>
              </button>
            </div>
          </header>

          {/** Previous header with loading skeleton **/}
          {/**
          {isLoading ? (
            <PageHeaderSkeleton />
          ) : (
            <header className={styles.header}>
              <h1 
                className={styles.title}
                id="page-title"
              >
                Supply GPU
              </h1>
              <button
                className={styles.addButton}
                onClick={handleOpenAddModal}
                aria-describedby="page-title"
              >
                <img alt="" className={styles.addIcon} src="/icons/left_icon.svg" />
                <div>Add a machine</div>
              </button>
            </header>
          )}
          **/}

          <main 
            role="main"
            aria-labelledby="page-title"
            aria-live="polite"
            aria-busy={false}
          >
            {renderContent()}
          </main>
        </div>
      )}

        {isAddModalOpen && (
          <div
            id="add-worker-dialog"
            className={galleryStyles.modalOverlay}
            role="dialog"
            aria-modal="true"
            aria-labelledby="add-worker-title"
            onClick={handleCloseAddModal}
          >
            <div className={galleryStyles.modal} onClick={(e) => e.stopPropagation()}>
              <h2 id="add-worker-title" className={galleryStyles.modalTitle}>Add your worker</h2>
              <p className={galleryStyles.modalText}>
                To add your worker please contact us on <a href="https://t.me/swarmindCEO" target="_blank" rel="noopener noreferrer" className={galleryStyles.mailLink}>Telegram</a>
              </p>
              <div className={galleryStyles.modalActions}>
                <button type="button" className={galleryStyles.closeButton} onClick={handleCloseAddModal}>Close</button>
              </div>
            </div>
          </div>
        )}
    </ErrorBoundary>
  );
});

SupplyGpuPage.displayName = 'SupplyGpuPage';

export default SupplyGpuPage;
